import createClient from "openapi-fetch";
import type { paths } from "./api-schema";

/** An API error with the stable `code` the backend sends. The UI translates the code. */
export class ApiFail extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public extra: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

// Same-origin: Next proxies /api to the backend, so the session cookie just works.
// The custom header is the CSRF guard the backend requires on state-changing calls.
export const client = createClient<paths>({
  baseUrl: "",
  headers: { "X-Requested-With": "netro-web" },
});

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

client.use({
  onResponse({ response }) {
    if (response.status === 401) onUnauthorized?.();
    return undefined;
  },
});

function toFail(status: number, error: unknown): ApiFail {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const { code, message, ...extra } = detail as Record<string, unknown>;
    return new ApiFail(status, String(code ?? "unknown"), String(message ?? "Request failed"), extra);
  }
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: string; loc?: unknown[] } | undefined;
    return new ApiFail(status, "validation", first?.msg ?? "Invalid input", { loc: first?.loc });
  }
  return new ApiFail(status, status === 429 ? "account_locked" : "unknown", typeof detail === "string" ? detail : "Request failed");
}

type Result<T> = { data?: T; error?: unknown; response: Response };

/** Unwrap an openapi-fetch result: return the data or throw an ApiFail. */
export async function call<T>(promise: Promise<Result<T>>): Promise<T> {
  let result: Result<T>;
  try {
    result = await promise;
  } catch {
    throw new ApiFail(0, "network", "Cannot reach the server.");
  }
  if (!result.response.ok) throw toFail(result.response.status, result.error);
  return result.data as T;
}

/** Like `call`, for endpoints that return a file (request with `parseAs: "blob"`). */
export async function callBlob(promise: Promise<Result<unknown>>): Promise<Blob> {
  let result: Result<unknown>;
  try {
    result = await promise;
  } catch {
    throw new ApiFail(0, "network", "Cannot reach the server.");
  }
  if (!result.response.ok) throw toFail(result.response.status, result.error);
  return result.data as Blob;
}

export function asFail(e: unknown): ApiFail {
  return e instanceof ApiFail ? e : new ApiFail(0, "unknown", e instanceof Error ? e.message : String(e));
}

/** POST a multipart form (a file upload) with progress. openapi-fetch cannot report upload progress. */
export function uploadForm<T>(path: string, form: FormData, onProgress?: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    xhr.setRequestHeader("X-Requested-With", "netro-web");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiFail(0, "network", "Cannot reach the server."));
    xhr.onload = () => {
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = undefined;
      }
      if (xhr.status === 401) onUnauthorized?.();
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else reject(toFail(xhr.status, body));
    };
    xhr.send(form);
  });
}

/** Open a downloaded blob in a new tab, or save it when the browser blocks the tab. */
export function openBlob(blob: Blob, filename: string, forceDownload = false): boolean {
  const url = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
  if (!forceDownload) {
    const w = window.open(url, "_blank");
    if (w) return true;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  return false;
}
